import { Fragment, type ReactNode } from "react"

import { DetectionPill } from "@/components/terminal/detection-pill"
import {
  formatDetectionDay,
  formatTerminalDetectionDate,
} from "@/lib/terminal/date"
import { detectionTypeLabel } from "@/lib/terminal/detection-type-label"
import type { TerminalDetectionView } from "@/lib/terminal/file-view"

// A Detection as its rail card and a reassessment's popover both read it:
// the heading (type pill, title, event day, then whatever the caller adds
// beneath the day) and, once opened, the summary and its sources. The
// caller sets --terminal-detection-color for the pill.
export const DetectionHeading = ({
  detection,
  children,
}: {
  detection: TerminalDetectionView
  children?: ReactNode
}) => (
  <>
    <div className="mb-1 flex items-center gap-1.5">
      <DetectionPill label={detectionTypeLabel(detection)} />
    </div>

    <h3 className="type-finding">{detection.title}</h3>

    <div className="mt-1 flex flex-col items-start gap-0.5">
      <span className="type-count text-text-disabled whitespace-nowrap">
        {formatDetectionDay(detection) ?? formatTerminalDetectionDate(null)}
      </span>
      {children}
    </div>
  </>
)

export const DetectionSummary = ({
  detection,
}: {
  detection: TerminalDetectionView
}) => (
  <>
    <p className="type-narrative text-muted-foreground">{detection.summary}</p>
    <DetectionSources sources={detection.sources} />
  </>
)

// Every institution behind the finding, each by its short name with the
// official one on hover, linked when a person can open its record. A review
// pattern names none.
const DetectionSources = ({
  sources,
}: Pick<TerminalDetectionView, "sources">) => {
  if (sources.length === 0) return null
  return (
    <span className="text-muted-foreground text-xs">
      Source:{" "}
      {sources.map((source, index) => {
        const text = [source.name.shortName, source.reference]
          .filter(Boolean)
          .join(" · ")
        return (
          <Fragment key={source.name.shortName}>
            {index > 0 && ", "}
            {source.url ? (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                title={source.name.officialName}
                onClick={(event) => event.stopPropagation()}
                className="text-terminal-accent no-underline hover:underline"
              >
                {text} ↗
              </a>
            ) : (
              <span title={source.name.officialName}>{text}</span>
            )}
          </Fragment>
        )
      })}
    </span>
  )
}
